import ExtendedEventEmitter from '../../events/ExtendedEventEmitter';
import { localStorageService } from './LocalStorageService';
import Logger from '../../utils/logging/Logger';
import Keycloak from 'keycloak-js';
import TrackingService from '../../tracking/services/TrackingService';
import toObject from '../../utils/toObject/toObject';
import Permissions from '../models/auth/Permissions';
import { CountryUtils } from '@legacy-modules/utils/tours/CountryUtils';
import {
  lmaReduxStore,
  selectAuth,
  AuthState,
  selectRootOrgKeys,
  dashboardSlice,
  overviewSlice,
  authSlice,
} from '@redux';
import { ConfigKey, getEnvVariable, getKeycloakConfiguration } from './EnvironmentConfigurationService';

type Root = 'overview' | 'dashboard' | 'stoppdetail';
type RootOrgKeys = { [k in Root]: string };
type UserDetails = {
  displayName: string;
  email: string;
  username: string;
};
export type AuthStatusPayload = {
  loggedIn: boolean;
  username?: string;
  permissions?: Array<Array<string>>;
  rootOrgKey?: string;
  rootOrgKeys?: RootOrgKeys;
  tokenId?: string;
  userDetails: UserDetails;
};

export enum AuthEvent {
  EVENT_LOGGED_IN = 'auth.loggedIn',
  EVENT_LOGGED_OUT = 'auth.loggedOut',
}

export default class AuthService {
  authStateProvider: () => AuthState;
  trackingService: TrackingService;
  _eventEmitter: ExtendedEventEmitter;
  logger: Logger;
  keycloak: Keycloak.KeycloakInstance;
  tokenRefresh: number;
  httpPath: string;

  constructor(trackingService: TrackingService) {
    this.authStateProvider = () => selectAuth(lmaReduxStore.getState());
    this._eventEmitter = new ExtendedEventEmitter();
    this.trackingService = trackingService;
    this.logger = Logger.getInstance('AuthService');
    this.httpPath = 'authentication';

    const { url, realm, resource } = getKeycloakConfiguration();
    this.keycloak = Keycloak({
      url: url,
      realm: realm,
      clientId: resource,
    });
  }

  get isAuthenticated(): boolean {
    return this.authStateProvider().loggedIn;
  }

  get rootOrgKeys() {
    return this.authStateProvider().rootOrgKeys;
  }

  async recoverTokenFromStorage() {
    const authData = localStorageService.get<AuthState>('authState');
    if (authData?.token) {
      authSlice.actions.setToken(authData.token);
    }
  }

  async _initKeyCloak(): Promise<void> {
    this.logger.info('AuthService', 'Trying to init Keycloak.');
    this.keycloak.onAuthSuccess = this.authenticateWithBackend.bind(this);
    this.keycloak.onAuthRefreshSuccess = this.refreshBackend.bind(this);
    this.keycloak.onTokenExpired = this.refreshToken.bind(this);
    const options = {
      onLoad: 'login-required' as const,
      promiseType: 'native',
      checkLoginIframe: false,
    };

    return new Promise((resolve, reject) => {
      this.keycloak.init(options).then(async (authenticated) => {
        this.logger.info('Initialized keycloak. Authenticated? ' + authenticated);
        if (authenticated) {
          lmaReduxStore.dispatch(authSlice.actions.setToken(this.keycloak.token));
          this.logger.info('AuthService', 'Initialized in _initKeyCloak', this.authStateProvider().initialized);
          resolve();
        } else {
          this.logger.error('AuthService', 'Failed to init Keycloak.');
          reject(new Error('Failed to init Keycloak'));
        }
      });
    });
  }

  async init() {
    await this.recoverTokenFromStorage();
    this.logger.info('AuthService', 'Initialized: ' + this.authStateProvider().initialized);
    if (!this.authStateProvider().initialized) {
      try {
        await this._initKeyCloak();
        if (this.tokenRefresh == null) {
          this.logger.info('AuthService', 'Setting window timer to refresh token');
          this.tokenRefresh = window.setInterval(this.refreshToken.bind(this), 5000);
        }
      } catch (e) {
        await this.logout();
      }
    } else {
      await this.authenticateWithBackend();
    }
  }

  refreshToken() {
    this.keycloak
      .updateToken(60)
      .then((refreshed) => refreshed && lmaReduxStore.dispatch(authSlice.actions.setToken(this.keycloak.token)))
      .catch(() => this.logger.warn('AuthService refreshToken failure'));
  }

  async refreshBackend(): Promise<void> {
    await this.sendAuthenticationRequestToBackend(false);
  }

  async authenticateWithBackend(): Promise<void> {
    await this.sendAuthenticationRequestToBackend(true);
  }

  async sendAuthenticationRequestToBackend(isLoginRequest: boolean): Promise<void> {
    const requestSuccessful = await (isLoginRequest ? this.requestLogin() : this.requestTokenRefresh());
    if (!requestSuccessful) {
      this.logger.info('Could not authenticate to backend, retrying in 10 seconds');
      window.setTimeout(() => this.sendAuthenticationRequestToBackend(isLoginRequest), 10000);
    }
  }

  async requestLogin(): Promise<boolean> {
    const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/${this.httpPath}/login`);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.keycloak.token}` },
      });
      if (response.ok) {
        const authResult: AuthStatusPayload = await response.json();
        return this._processAuthResponse(authResult);
      } else {
        await this._trackLogin(false, `Could not authenticate with token ${this.keycloak.token}`);
        await this.logout();
        return false;
      }
    } catch (e) {
      this.logger.error('Could not login due to network error.');
      return false;
    }
  }

  async requestTokenRefresh(): Promise<boolean> {
    const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/${this.httpPath}/refresh`);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.keycloak.token}`, token: lmaReduxStore.getState().auth.tokenId },
      });
      return response.ok;
    } catch (e) {
      this.logger.error('Could not refresh token due to network error.');
      return false;
    }
  }

  async _processAuthResponse(payload): Promise<boolean> {
    if (payload.loggedIn) {
      lmaReduxStore.dispatch(authSlice.actions.setLoggedIn(true));
      lmaReduxStore.dispatch(authSlice.actions.setTokenId(payload.tokenId));
      if (!this.authStateProvider().initialized) {
        lmaReduxStore.dispatch(authSlice.actions.setUserDetails(payload.userDetails));
        lmaReduxStore.dispatch(authSlice.actions.setPermissions(payload.permissions || []));
        const rootOrgKeys = payload.rootOrgKeys;
        const defaultRootOrgKey = payload.rootOrgKey;
        const overview = rootOrgKeys?.overview || defaultRootOrgKey;
        const dashboard = rootOrgKeys?.dashboard || defaultRootOrgKey;
        const stoppdetail = rootOrgKeys?.stoppdetail || defaultRootOrgKey;
        lmaReduxStore.dispatch(
          authSlice.actions.setRootOrgKeys({
            overview,
            dashboard,
            stoppdetail,
          })
        );
        lmaReduxStore.dispatch(overviewSlice.actions.setRootOrgKey(overview));
        lmaReduxStore.dispatch(overviewSlice.actions.updateOrgKeys([]));
        lmaReduxStore.dispatch(dashboardSlice.actions.setOrgKey(dashboard));

        localStorageService.set('authState', toObject(this.authStateProvider()));

        lmaReduxStore.dispatch(authSlice.actions.setInitialized(true));
        this._eventEmitter.emit(AuthEvent.EVENT_LOGGED_IN);
        await this._trackLogin(true);
      }
    } else {
      await this._trackLogin(false);
      await this.logout();
    }
    return this.authStateProvider().loggedIn;
  }

  async _trackLogin(success: boolean, error: string | null = null): Promise<void> {
    await this.trackingService.sendEvent('last-mile-analytics.auth.login', {
      loginInfo: { success, error },
    });
  }

  async logout(): Promise<void> {
    this.logger.info(`Logging user '${this.authStateProvider().userDetails.username}' out`);
    window.clearInterval(this.tokenRefresh);
    delete this.tokenRefresh;
    if (this.isAuthenticated) {
      await this.trackingService.sendEvent('last-mile-analytics.auth.logout');
    }
    lmaReduxStore.dispatch(authSlice.actions.logout());
    localStorageService.remove('authState');
    this._eventEmitter.emit(AuthEvent.EVENT_LOGGED_OUT);
    this.keycloak.logout();
  }

  can(permission: string): boolean {
    const permissions = new Permissions();
    permissions.hydrateFrom(this.authStateProvider().permissions);
    return permissions.can(permission);
  }

  cannot(permission: string): boolean {
    return !this.can(permission);
  }

  // check if an internal employee has rights to access certain visibilities
  // such as stop details or signatures
  canAccessStop(orgPath: string[]): boolean {
    const stoppdetail = selectRootOrgKeys(lmaReduxStore.getState())?.stoppdetail;

    // no stop access allowed anywhere, for example marketing employees
    if (!orgPath || !stoppdetail) {
      return false;
    }

    // access to every stop, for example devs or project leads
    if (stoppdetail?.some((key) => CountryUtils.isCountry(key))) {
      return true;
    }

    // access to stops of tours belonging to certain org, in case stopp detail root key
    // represents a certain area (oa) or zsb (oz)
    return orgPath.some((orgKey) => stoppdetail?.includes(orgKey));
  }

  get eventEmitter() {
    return this._eventEmitter;
  }

  get isHermesInternalUser() {
    return this.authStateProvider().userDetails.email.endsWith('@hermesworld.com');
  }
}
